import { Check, Building2, MapPin, ClipboardList, Wrench, Users, Globe, UserCheck } from "lucide-react";

import { useLang } from "@/lib/i18n";

const ICONS = { Building2, MapPin, ClipboardList, Wrench, Users, Globe, UserCheck };

function H2({ children, testid }) {
  return (
    <h2 data-testid={testid} className="font-display text-2xl sm:text-3xl leading-tight text-[#14352a]">
      {children}
    </h2>
  );
}

function Bullets({ items, testid }) {
  const { t } = useLang();
  return (
    <ul data-testid={testid} className="mt-4 space-y-2">
      {items.map((b) => (
        <li key={b} className="flex gap-3 text-base text-[#14352a] leading-relaxed">
          <span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#9a7218]" />
          {t(b)}
        </li>
      ))}
    </ul>
  );
}

export default function CaseStory({ story, org }) {
  const { t } = useLang();
  if (!story) return null;
  return (
    <>
      <section data-testid="case-story" className="py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8 space-y-10">
            <div>
              <H2 testid="case-in-short">{t("In short")}</H2>
              <div className="mt-4 space-y-4 text-base sm:text-lg text-[#14352a] leading-relaxed">
                {story.inShort.map((p) => <p key={p}>{t(p)}</p>)}
              </div>
            </div>
            <div>
              <H2 testid="case-challenge">{t("The challenge")}</H2>
              <p className="mt-4 text-base sm:text-lg text-[#14352a] leading-relaxed">{t(story.challenge.text)}</p>
              <Bullets items={story.challenge.bullets} testid="case-challenge-bullets" />
            </div>
            <div>
              <H2 testid="case-needed">{org} {t("needed a solution that could:")}</H2>
              <Bullets items={story.needed} testid="case-needed-bullets" />
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div data-testid="case-fact-card" className="lg:sticky lg:top-24 bg-[#f2ece4] rounded-2xl p-6 sm:p-7">
              <div className="font-display text-xl text-[#14352a]">{org}</div>
              <dl className="mt-6 space-y-6">
                {story.facts.map((f) => {
                  const Icon = ICONS[f.icon];
                  return (
                    <div key={f.label} className="flex gap-4">
                      <span className="h-11 w-11 shrink-0 grid place-items-center rounded-lg bg-white text-[#14352a]">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </span>
                      <div>
                        <dt className="text-sm font-medium text-[#14352a]">{t(f.label)}</dt>
                        <dd className="mt-0.5 text-sm text-[#55625b] leading-relaxed">{t(f.value)}</dd>
                      </div>
                    </div>
                  );
                })}
              </dl>
            </div>
          </aside>
        </div>
      </section>

      <section data-testid="case-solution" className="py-10 lg:py-14 border-t border-[#e5e0d8] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
          <div className="max-w-4xl">
            <div className="font-mono-label text-[11px] text-[#9a7218]">{t("The solution")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight text-[#14352a]">{t(story.solution.heading)}</h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-[#14352a] leading-relaxed">
              {story.solution.paragraphs.map((p) => <p key={p}>{t(p)}</p>)}
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {story.columns.map((c, i) => (
              <div key={c.title} data-testid={`case-column-${i}`}>
                <h3 className="font-display text-xl text-[#14352a] leading-snug">{t(c.title)}</h3>
                <ul className="mt-5 space-y-3.5">
                  {c.items.map((it) => (
                    <li key={it} className="flex gap-3 text-base text-[#14352a] leading-snug">
                      <Check className="h-5 w-5 shrink-0 text-[#9a7218]" strokeWidth={2.2} />
                      {t(it)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
